import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { extractProjectEstimationRevisions } from "../utils/revisionEntries";
import { drawingsOf, isPvWbs, wbsLabel } from "../utils/treeLabels";
import {
  pipingWbsTabs,
  projectTypeDiscipline,
} from "../../../../../constants/projectTypes";
import { useTreeViewContext } from "../TreeViewContext";
import { wbsNodeId } from "../utils/treeNodeIds";
import {
  estimationTabLabel,
  latestRevisionNumber,
  nextRevisionNumber,
} from "../utils/treeRevisionHelpers";
import { AddActionIcon, AddActionLabel } from "./AddAction";
import {
  AddRevisionButton,
  ProjectEstimationNode,
  PvInputNode,
  SpecNode,
} from "./RevisionNodes";
import { DrawingsFolderNode } from "./DrawingNodes";
import { ModelingFolderNode } from "./ModelingNodes";

// The documents filed under one revision of one WBS - the rows of the
// Document Register whose WBS is this one and whose Latest Revision is
// this revision, named by the identifier each was created under.
//
// docsByWbs is this WBS's own register (fetched when the WBS expands), so
// only the revision has to be matched here. "loading" is a string rather
// than an array while that fetch is in flight.
const revisionDocsForWbs = (docsByWbs, wbs, revision) => {
  const docs = docsByWbs[wbs.id];
  if (!Array.isArray(docs)) return [];

  const wanted = String(revision ?? "").trim();
  return docs.filter(
    (doc) => String(doc?.revision ?? "").trim() === wanted,
  );
};

/**
 * `allowAdd` is whether this WBS offers "Add Rev" and "Add Document",
 * `allowOpen` whether its revisions, documents and Spec open the module
 * pages they name, and `showModelingBranch` whether the WBS carries the
 * "3D Modeling" branch (3D Modeling -> 2D Drawing / BOM) whose BOM holds
 * the project's drawings.
 *
 * The first two are off under Design Automation, where the WBS listed are
 * the records the automation software wrote under a legacy type: the tree is
 * showing them, not inviting work to be filed against them - and what their
 * revisions represent is not the Estimation pages these same rows open in
 * the Estimation section, so they open nothing until that is settled. The
 * modeling branch is on there: its BOM drawings are the one thing under
 * these WBS that opens, straight to BomPage with no confirmation.
 */
export const WbsNode = ({
  plant,
  wbs,
  allowAdd = true,
  allowOpen = true,
  showModelingBranch = false,
}) => {
  const {
    treeData,
    pickerMode,
    nodeTargetRef,
    openDocumentRegister,
    wbsOperations,
  } = useTreeViewContext();
  const { docsByWbs, pvInputsByWbs, drawingsByWbs } = treeData;
  const { openWbsMenu } = wbsOperations;

  const id = wbsNodeId(wbs.id);
  nodeTargetRef.current[id] = { kind: "wbs", plant, wbs };
  // Empty for anything that is not a Piping WBS - PV and Structural pick
  // their folders off isPvWbs and the discipline, as before.
  const pipingTabs = pipingWbsTabs(wbs.type);
  const pvInputs = isPvWbs(wbs) ? pvInputsByWbs[wbs.id] : undefined;
  // Every WBS whose revisions are Estimation ones: Piping (a "PIE" or
  // legacy "pip" WBS), Structural, and Instrumentation - whose Estimation
  // works like Piping's, in its own module (Ams/Instrumentation).
  const hasEstimationRevisions =
    !isPvWbs(wbs) &&
    (pipingTabs.includes("estimation") ||
      projectTypeDiscipline(wbs.type) === "struc" ||
      projectTypeDiscipline(wbs.type) === "ins");
  // Already embedded in wbs.revisions from the same WBS-list fetch that
  // populated this node - no separate loading state needed, unlike PV's
  // revisions, which come from their own /PV/all call. The plant's name is
  // the first half of every Rev UID (see extractProjectEstimationRevisions).
  const estimationRevisions = hasEstimationRevisions
    ? extractProjectEstimationRevisions(wbs, { plantName: plant?.name })
    : [];

  // Just the number ("0", "1", …), not a revision's document UID - it
  // seeds the register's "Latest Revision" field (see openDocumentRegister).
  const latestRevision = hasEstimationRevisions
    ? (estimationRevisions[estimationRevisions.length - 1]?.revision ?? "")
    : Array.isArray(pvInputs)
      ? latestRevisionNumber(pvInputs)
      : "";
  // Only for the document-count badge on the WBS row: the Documents folder
  // was taken out of the tree, which now shows a WBS's working data
  // (Spec / Input Data / Estimation) plus its 2D Drawings folder.
  const docs = docsByWbs[wbs.id];

  const revisionDocs = (revision) =>
    revisionDocsForWbs(docsByWbs, wbs, revision);

  const categories = [];

  if (isPvWbs(wbs)) {
    // PV's input-data revisions hang straight off the WBS - the folder that
    // used to wrap them only ever held this one list, and its "+" now sits
    // on the WBS row itself (see the label below).
    if (pvInputs === "loading") {
      categories.push(
        <TreeItem
          key={`${id}-input-loading`}
          nodeId={`${id}-input-loading`}
          label={
            <Typography variant="caption" color="text.secondary">
              Loading input data…
            </Typography>
          }
        />,
      );
    } else if (Array.isArray(pvInputs) && pvInputs.length > 0) {
      const latest = latestRevisionNumber(pvInputs);
      pvInputs.forEach((entry) =>
        categories.push(
          <PvInputNode
            key={`${wbs.id}-${entry.revision}`}
            plant={plant}
            wbs={wbs}
            revisionEntry={entry}
            revisionDocs={revisionDocs(entry.revision)}
            allowOpen={allowOpen}
            addRevisionAction={
              allowAdd && String(entry.revision) === latest ? (
                <AddRevisionButton
                  plant={plant}
                  wbs={wbs}
                  revision={nextRevisionNumber(pvInputs)}
                  subTabLabel="Input Data"
                />
              ) : null
            }
          />,
        ),
      );
    } else if (Array.isArray(pvInputs)) {
      categories.push(
        <TreeItem
          key={`${id}-input-empty`}
          nodeId={`${id}-input-empty`}
          label={
            <Typography variant="caption" color="text.secondary">
              No input data revisions
            </Typography>
          }
        />,
      );
    } else {
      // Not loaded yet - the revisions are only fetched when this WBS is
      // expanded (see handleNodeToggle), so without a child here the WBS
      // would have no expand arrow and could never load them.
      categories.push(
        <TreeItem
          key={`${id}-placeholder`}
          nodeId={`${id}-placeholder`}
          label=""
        />,
      );
    }
  } else {
    // Piping's Spec and Estimation are separate WBS now, so a WBS carries
    // only the parts its own type opens: Spec for a "spec" WBS, Estimation
    // for a "PIE" one, and both for a legacy "pip" WBS, which still holds
    // the two tabs together. Structural and Instrumentation never had a
    // Spec half.
    if (pipingTabs.includes("spec")) {
      // A plain node rather than a folder: there is only ever the one Spec
      // to open, so a folder around a single child would read "Spec > Spec"
      // and cost a click.
      categories.push(
        <SpecNode
          key={wbs.id}
          plant={plant}
          wbs={wbs}
          allowOpen={allowOpen}
        />,
      );
    }

    if (hasEstimationRevisions) {
      if (estimationRevisions.length > 0) {
        // The Estimation folder that used to wrap these is gone: it only
        // ever held this one list, so the revisions now hang straight off
        // the WBS exactly as PV's do, each with its own BOM leaves.
        estimationRevisions.forEach((entry, index) =>
          categories.push(
            <ProjectEstimationNode
              key={`${wbs.id}-${entry.revision}`}
              plant={plant}
              wbs={wbs}
              revisionEntry={entry}
              topTabLabel={estimationTabLabel(wbs.type)}
              revisionDocs={revisionDocs(entry.revision)}
              allowOpen={allowOpen}
              // Only the last revision in the list - the one a new one is
              // copied from. `revision: undefined` lets the module page
              // number the new one off the estimation data it just loaded,
              // rather than the tree guessing from the WBS list.
              addRevisionAction={
                allowAdd && index === estimationRevisions.length - 1 ? (
                  <AddRevisionButton
                    plant={plant}
                    wbs={wbs}
                    revision={undefined}
                    topTabLabel={estimationTabLabel(wbs.type)}
                    wbsType={wbs.type}
                  />
                ) : null
              }
            />,
          ),
        );
      } else {
        categories.push(
          <TreeItem
            key={`${id}-estimation-empty`}
            nodeId={`${id}-estimation-empty`}
            label={
              <Typography variant="caption" color="text.secondary">
                No estimation revisions
              </Typography>
            }
          />,
        );
      }
    }
  }

  // 2D Drawings are out of the tree: a WBS shows its working data (Spec /
  // Input Data / Estimation) and nothing else.
  //
  // They stay in picker mode, which is not the tree being browsed but the
  // Validation page's Compare asking for a side to compare - a drawing is
  // one of the two things it can be given, and these nodes are the only
  // place a "drawing" selection comes from (see SelectionBar).
  if (pickerMode) {
    categories.push(
      <DrawingsFolderNode
        key={`${id}-drawings`}
        plant={plant}
        wbs={wbs}
        drawings={drawingsOf(wbs, drawingsByWbs)}
      />,
    );
  }

  // The "3D Modeling" branch - Design Automation's WBS carry it, so their
  // drawings live under the BOM folder there. The branch loads nothing of
  // its own: the drawings ride on the WBS record (see drawingsOf), and
  // picker mode keeps the 2D Drawings folder above instead.
  if (showModelingBranch && !pickerMode) {
    categories.push(
      <ModelingFolderNode
        key={`${id}-modeling`}
        plant={plant}
        wbs={wbs}
        drawings={drawingsOf(wbs, drawingsByWbs)}
      />,
    );
  }

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={
        <AccountTreeIcon fontSize="inherit" sx={{ color: "primary.main" }} />
      }
      label={
        <Box
          onContextMenu={
            // Not in picker mode: there the tree only reports a choice
            // back to a dialog, it doesn't edit anything.
            pickerMode ? undefined : (event) => openWbsMenu(event, plant, wbs)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            title={wbsLabel(wbs)}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {wbsLabel(wbs)}
          </Typography>
          {allowAdd &&
            isPvWbs(wbs) &&
            // Once revisions exist the button lives on the latest of them;
            // on the WBS row it only offers the very first one. PV's Rev.
            // dropdown lives on Input Data > Design, so that's where a new
            // revision opens.
            Array.isArray(pvInputs) &&
            pvInputs.length === 0 && (
              <AddRevisionButton
                plant={plant}
                wbs={wbs}
                revision={nextRevisionNumber(pvInputs)}
                subTabLabel="Input Data"
              />
            )}
          {allowAdd &&
            hasEstimationRevisions &&
            // Same rule as PV's above, now that the Estimation folder that
            // used to carry this button on an empty WBS is gone.
            estimationRevisions.length === 0 && (
              <AddRevisionButton
                plant={plant}
                wbs={wbs}
                revision={undefined}
                topTabLabel={estimationTabLabel(wbs.type)}
                wbsType={wbs.type}
              />
            )}
          {!pickerMode && allowAdd && (
            <IconButton
              size="small"
              title={
                Array.isArray(docs)
                  ? `${docs.length} document${docs.length === 1 ? "" : "s"} in this project's register — open it`
                  : "Open this project's Document Register"
              }
              onClick={(event) => {
                event.stopPropagation();
                openDocumentRegister(plant, wbs, latestRevision);
              }}
              sx={{ p: 0.4, borderRadius: 1 }}
            >
              <AddActionIcon color="warning.main" />
              <AddActionLabel>Add Document</AddActionLabel>
            </IconButton>
          )}
        </Box>
      }
    >
      {categories}
    </TreeItem>
  );
};
